<template lang="pug">
	.web-services--script-builder
		.flex-row.space-large
			.flex-col.flex-1.field
				.font-big.upper.grey {{$tCaption('Source structure')}}
				.view-area
					JsonViewer(ref="viewer", :value="jsonValue")
						template(v-slot:item-after="{ key, path, type, value }")
							.flex-row.space(v-if="selectorVisible(path, type, key)")
								.add-button.flex-row.align-center.center.clickable.hoverable(@click="addInfo(formatPath(path), key)", :class="{ 'active': info.selectedPath === path }")
									fas(:icon="info.selectedPath === path ? 'check' : (info.selectedPath ? 'plus' : 'arrow-right')", size="2xs")
			.flex-col.space-large.flex-1
				.font-big.upper.grey {{$tCaption('Selectors')}}
				.flex-col.space.view-area
					.font-small.upper.grey(v-if="resultIsObject") {{$tCaption('Fields')}}
					.flex-col.space
						.result-field.flex-col.space(v-for="(result, rIdx) in info.results")
							.flex-col
								.font-tiny.upper.grey {{$tCaption('Result field')}}
								.flex-row.justify
									b.font-tiny {{getPathDisplayValue(result.keyPath)}}
									.add-button.flex-row.align-center.center.clickable.hoverable(@click="removeResult(result, rIdx)")
										fas(icon="close", size="2xs")
							.array-item.flex-col(v-for="array in result.arrays")
								i.font-small {{$tCaption('Array operations')}} ({{array.path}}):
								ScriptBuilderArrayOperations(:operations="array.operations", :hideConvertToValue="true", :fields="array.fields", :valueType="array.valueType", :service="service", :hideFieldSelection="true",
									@preview="v => setPreviewOperation(array, v)")
							.flex-col(v-if="resultIsObject")
								.font-tiny.upper.grey {{$tCaption('Result field')}}
								DxTextBox(:value.sync="result.name", stylingMode="filled")
							.flex-col(v-if="result.fields && result.fields.length")
								.font-tiny.upper.grey {{$tCaption('Additional fields')}}
								.flex-col.space
									.flex-col.space(v-for="(field, fIdx) in result.fields")
										.flex-row.justify
											b.font-tiny {{getPathDisplayValue(field.keyPath)}}
											.add-button.flex-row.align-center.center.clickable.hoverable(@click="result.fields.splice(fIdx, 1)")
												fas(icon="close", size="2xs")
										.array-item.flex-col(v-for="array in getAvailableFieldArrays(result, field, fIdx)")
											i.font-small {{$tCaption('Array operations')}} ({{array.fullPath}}):
											ScriptBuilderArrayOperations(:operations="array.operations", :hideConvertToValue="false", :fields="array.fields", :valueType="array.valueType", :service="service", :hideFieldSelection="true",
												@preview="v => setPreviewOperation(array, v)")
						.array-item.flex-col(v-if="resultIsArray || info.resultArrayOperations.length")
							i.font-small {{$tCaption('Result array operations')}}:
							ScriptBuilderArrayOperations(:operations="info.resultArrayOperations", :fields="resultFields", :valueType="info.results[0] && info.results[0].valueType", :service="service",
								@preview="v => setPreviewOperation(info, v)")
			.flex-col.flex-1.space
				.flex-col
					.flex-row.space.align-center
						.font-big.upper.grey {{$tCaption('Result')}}
						.add-button.flex-row.align-center.center.clickable.hoverable(v-if="result", @click="downloadResult")
							fas(icon="download", size="2xs")
					.view-area.flex-col
						JsonViewer(ref="resultViewer", :value="result", :expanded="true", :expandDepth="3")
						DxCheckBox(v-if="resultIsArray", :text="$tCaption('Format to CSV')", :value.sync="info.formatToCsv")
</template>

<script>
import _ from 'lodash';
import DxButton from 'devextreme-vue/button';
import JsonViewer from 'json-viewer';
import DxCheckBox from 'devextreme-vue/check-box';
import DxSelectBox from 'devextreme-vue/select-box';
import DxTextBox from 'devextreme-vue/text-box';
import Popup from 'app-core/Popup.vue';
import DxNumberBox from 'devextreme-vue/number-box';
import ScriptBuilderArrayOperations from './ScriptBuilderArrayOperations.vue';
import ScriptBuilderObjectOperations from './ScriptBuilderObjectOperations.vue';
import dataOperations from './data-operations';
import { saveAs } from 'file-saver';

export default {
	translationCategory: 'webServiceCaller.ScriptBuilder',
	components: { DxButton, JsonViewer, DxCheckBox, DxSelectBox, DxTextBox, Popup, DxNumberBox, ScriptBuilderArrayOperations, ScriptBuilderObjectOperations },
	props: ['source', 'scriptInfo', 'service', 'params'],
	data() {
		return {
			info: _.assign({
				results: [],
				resultArrayOperations: [],
				selectedPath: null,
				formatToCsv: false,
				isArray: false
			}, this.scriptInfo || {}),
			resultTypes: ['single', 'array'],
			processingItem: null,
			dataTypes: ['date', 'object', 'string', 'number', 'bool', 'array'],
			result: null,
			convertsToValueOperations: ['aggregate', 'first', 'last', 'join'],
			arrayPreviews: [],
			script: null
		};
	},
	computed: {
		jsonValue() {
			return this.source;
		},
		resultsVisible() {
			return this.info.results?.length;
		},
		resultIsObject() {
			return this.info.results.length > 1;
		},
		resultIsArray() {
			return this.info.results.length;
		},
		resultFields() {
			let info = this.info.results[0];
			if (!info) return [];

			return [
				{ name: info.name, dataType: info.valueType }, 
				...info.fields.map(f => ({
					name: f.name,
					dataType: f.valueType
				}))
			];
		}
	},
	watch: {
		info: {
			handler() {
				this.calcResults();
			},
			deep: true
		}
	},
	mounted() {
		if (__DEBUG__)
			window.scriptBuilder = this;

		this.calcResults();
	},
	methods: {
		getAvailableFieldArrays(result, field, fieldIdx) {
			let arraysInFieldsBefore = {};
			result.fields.slice(0, fieldIdx).map(f => f.arrays.map(a => arraysInFieldsBefore[a.path] = true));

			return field.arrays.filter(f => !result.arrays.find(a => a.path === f.path) && !arraysInFieldsBefore[f.path]);
		},
		setPreviewOperation(arr, previewingOp) {
			let existingIdx = this.arrayPreviews.findIndex(f => f.array === arr);			
			if (existingIdx === -1) {
				this.arrayPreviews.push({ array: arr, operation: previewingOp });
			} else {
				this.arrayPreviews[existingIdx].operation = previewingOp;
			}

			this.calcResults();
		},
		downloadResult() {
			if (this.info.formatToCsv) {
				saveAs(new Blob([this.result]), 'result.csv');
			} else {
				saveAs(new Blob([JSON.stringify(this.result, void 0, 4)]), 'result.json');
			}
		},
		removeResult(item, idx) {
			this.info.results = [];
			this.info.selectedPath = null;
			this.info.resultArrayOperations = [];
		},
		formatPath(jsonPath) {
			return `${jsonPath}`.replace(/.[0-9][0-9]?[0-9]?/g,'.0');
		},
		removeInfo(path) {

		},
		getDataType(val) {
			if (Array.isArray(val)) return 'array';
			if (typeof(val?.getUTCDay) === 'function') return 'date';
			if (typeof(val) === 'number') return 'number';
			if (typeof(val) === 'boolean') return 'bool';
			if (typeof(val) === 'object') return 'object';

			return 'string';
		},
		getArrInfo(path) {
			let values = this.getValue(path.replace(/.0/g, '[0]')) || [];
			const val = values[0];
			if (!val) return;

			let fields = [];

			let getFields = (v, parents) => {
				if (v && typeof(v) === 'object') {
					let prefix = (parents || '') + (parents ? '.' : '');
					let availableFields = Object.keys(v).filter(f => !Array.isArray(v[f]));
					fields.push(...availableFields.map(f => ({
						name: prefix + f,
						dataType: this.getDataType(val[f]),
						value: v,
						isNested: !!parents
					})));

					availableFields.map(f => getFields(v[f], prefix + f));
				}
			}

			getFields(val);

			return {
				fields,
				values,
				valueType: this.getDataType(val)
			}
		},
		buildInfo(path, key) {
			const split = `${path}`.split('.');
			const isArray = split.slice(-1)[0] == '0' || split.slice(-2)[0] == '0';

			const arraysList = `${path}`.split('.0');
			const arrays = arraysList.slice(0, arraysList.length - 1).map((f, idx) => {
				let fullPath = arraysList.slice(0, idx + 1).join('.0');
				let arrInfo = this.getArrInfo(fullPath);

				return {
					path: f,
					fullPath,
					operations: [],
					...arrInfo
				}
			});

			const processProp = (obj, key) => {
				const val = obj && obj[key];
				
				var prop = {
					key,
					type: typeof(val)
				};

				return prop;
			}

			let objectProps = null;
			let sampleValue = this.getValue(path.replace(/.0/g, '[0]'));
			if (typeof(sampleValue) === 'object') {
				objectProps = Object.keys(sampleValue).map(f => processProp(sampleValue, f));
			}
			let valueType = this.getDataType(sampleValue);

			const parentPath = (path || '').slice(0, path?.length - key?.length - 1);
			let parentLevels = [];
			let parentPathSplit = (parentPath || '').split('.').filter(f => f);
			while(parentPathSplit.length > 0) {
				let parentValue = this.getValue(parentPathSplit.join('.').replace(/.0/g, '[0]'));
				if (!Array.isArray(parentValue)) {
					parentLevels.push(Object.keys(parentValue).map(f => processProp(parentValue, f)));
				}
				parentPathSplit = parentPathSplit.slice(0, parentPathSplit.length - 1);
			}

			let objectOperations = [];

			return {
				type: isArray ? 'array' : 'object',
				valueType,
				key: key,
				keyPath: path,
				objectProps,
				name: this.idToReadable(this.toVarName(path)),
				arrays,
				parentPath,
				parentLevels,
				objectOperations,
				holdingArrayPath: arrays.slice(-1)[0]?.fullPath
			}
		},
		idToReadable(str) {
			if (!str) return '';
			let result = '';
			let makeUpper = false;
			for(var i = 0; i < str.length; i++) {
				if (str[i].match(/[ \_\.+!=]/)) {
					makeUpper = true;
					result += '';
				} else {
					result += makeUpper ? str[i].toUpperCase() : str[i];
					makeUpper = false;
				}
			}
			return result;
		},
		addInfo(path, key, parentInfo) {
			if (__DEBUG__)
				console.debug('Toggle info', { path, key });
			
			if (!this.info.selectedPath) {
				this.info.selectedPath = path;
				let info = this.buildInfo(path, key);
				info.fields = [];
				this.info.results = [info];
			} else {
				if (this.info.selectedPath === path) {
					this.info.results = [];
					this.info.selectedPath = null;
					return;
				} else {
					this.info.results[0].fields.push(this.buildInfo(path, key, parentInfo));
				}
			}
		},
		selectorVisible(jsonPath, type, key) {
			return type !== 'array' && `${key}`.replace(/[0-9]/g, '') || key == 0;
		},
		getPathDisplayValue(path) {
			return `${path || ''}`.replace(/.[0-9][0-9]?[0-9]?/g, '');
		},
		getValue(path) {
			const evalText = `
				var func = ($) => {
					return ${path};
				};

				func(this.data, this.params);
			`;

			if (__DEBUG__)
				console.debug('getValue', { path });

			let results = null;
			try {
				results = (function() {
					return eval(evalText);
				}).apply({
					data: this.jsonValue
				});
			} catch(ex) {
				results = `${ex}`;
			}

			return results;
		},
		buildArrayOperations(varName, operations) {
			if (__DEBUG__)
				console.debug('buildArrayOperations', { operations });
			let makeCommaList = (val, asArray) => {
				let arr = (typeof(val) === 'string' ? (val || '').split(',').filter(f => f) : val).map(f => `'${f}'`);
				if (asArray) return arr || [];
				return arr.join(',') || '';
			};
			let prepareFormula = (formula, isCode) => {
				if (!isCode) {
					formula = `\`${formula}\``;
				}
				[...((formula || '0').match(/\[(.*?)\]/g) || [])].map(f => {
					if (isCode) {
						formula = formula.replace(f, 'f.' + f.replace(/[\[\]]/g, ''));
					} else {
						formula = formula.replace(f, '${f.' + f.replace(/[\[\]]/g, '') + ' || \'\'}');
					}
				});
				return formula;
			}
			let scripts = [];
			if (operations?.length) {
				let arrPath = varName;
				let convertedToValue = false;
				operations.map(op => {
					if (convertedToValue) return;

					let hasCondition = false;
					if (op?.condition?.active) {
						scripts.push(``);
						
						let params = op?.condition?.paramValues || {};
						let activeParams = Object.keys(params).filter(f => params[f]);

						if (activeParams.length) {
							hasCondition = true;
							scripts.push(`if (${activeParams.map(f => `"@${f}" == ${JSON.stringify(params[f])}`).join(' && ')}) {`);
						}
					}

					switch(op.name) {
						case 'skip':
							scripts.push('');
							scripts.push('//SKIP');
							scripts.push(`${arrPath} = ${arrPath}.slice(${op.props.count.value}, 99999);`);
							break;
						case 'take':
							scripts.push('');
							scripts.push('//TAKE');
							scripts.push(`${arrPath} = ${arrPath}.slice(0, ${op.props.count.value});`);
							break;
						case 'sort':
							scripts.push('');
							scripts.push('//SORT');
							if (op.props.fields?.value?.length) {
								scripts.push(`${arrPath} = _.orderBy(${arrPath}, [${makeCommaList(op.props.fields?.value, true)}], [${makeCommaList(op.props.fields?.value, true).map(() => op.props.desc.value ? '"desc"' : '"asc"')}]);`);
							} else {
								scripts.push(`${arrPath} = _.orderBy(${arrPath}, void 0, [${op.props.desc.value ? '"desc"' : '"asc"'}]);`);
							}
							break;
						case 'group':
							scripts.push('');
							scripts.push('//GROUP');
							if (op.props.groups?.value?.length) {
								scripts.push(`${arrPath} = await dataOperations.groupDataSource(${arrPath}, [${makeCommaList(op.props.groups?.value)}], [${makeCommaList(op.props.aggregates?.value)}], '${op.props.aggregationType?.value}');`);
							} else {
								scripts.push(`${arrPath} = _.uniq(${arrPath});`);
							}
							break;
						case 'filter':
							scripts.push('');
							scripts.push('//FILTER');
							if (op.props.criteria?.value?.length) {
								scripts.push('');
								scripts.push('//JOIN');
								scripts.push(`${arrPath} = await dataOperations.filterDataSource(${arrPath}, ${JSON.stringify(op.props.criteria?.value)});`);
							}
							break;
						case 'aggregate': {
								scripts.push('');
								scripts.push('//AGGREGATE');
								let mapField = op.props.field?.value ? `.map(f => f.${op.props.field?.value})` : '';
								if (op.props.operation.value === 'count') {
									scripts.push(`${arrPath} = ${arrPath}.length;`);
								} else if (op.props.operation.value === 'avg') {
									scripts.push(`${arrPath} = _.sum(${arrPath}${mapField})/${arrPath}.length;`);
								} else {
									scripts.push(`${arrPath} = _.${op.props.operation.value}(${arrPath}${mapField});`);	
								}							
								break;
							}
						case 'unique': {
							scripts.push('');
							scripts.push('//UNIQUE');
							let mapField = op.props.field?.value ? `.map(f => f.${op.props.field?.value})` : '';
							scripts.push(`${arrPath} = _.uniq(${arrPath}${mapField});`);
							break;
						}
						case 'join': {
								scripts.push('');
								scripts.push('//JOIN');
								let mapField = op.props.field?.value ? `.map(f => f.${op.props.field?.value})` : '';
								scripts.push(`${arrPath} = ${arrPath}?.join ? ${arrPath}${mapField}.join('${op.props.delimeter.value}') : ${arrPath};`);							
								break;
							}
						case 'first':
							scripts.push('');
							scripts.push('//FIRST');
							scripts.push(`${arrPath} = (${arrPath})[0];`);
							break;
						case 'last':
							scripts.push('');
							scripts.push('//LAST');
							scripts.push(`${arrPath} = (${arrPath}).slice(-1)[0];`);
							break;
						case 'combine':
							if (op.props.fieldName?.value && op.props.formula?.value) {
								scripts.push('');
								scripts.push('//COMBINE');
								scripts.push(`${arrPath} = (${arrPath}).map(f => ({ ...f, "${op.props.fieldName?.value || 'NewField'}": ${prepareFormula(op.props.formula.value, op.props.isCode?.value)} }));`);
							}
							break;
						case 'format':
							{
								let field = op.props.field?.value;
								
								switch(op.props.dataType.value) {
									case 'date':
										if (op.props.format?.value) {
											scripts.push('');
											scripts.push('//FORMAT');

											scripts.push('');
											scripts.push('{');

											scripts.push(...this.getDateFormatScript(op.props.format?.value, op.props.utc?.value, 'formatDate'));
											if (field) {
												scripts.push(`${arrPath}.map(f => f.${field} = formatDate(new Date(f.${field})))`);
											} else {
												scripts.push(`${arrPath} = ${arrPath}.map(f => formatDate(new Date(f)))`);
											}
											
											scripts.push('}');
											scripts.push('');
										}
										break;
									case 'number': {
										scripts.push('');
										scripts.push('//FORMAT');

										scripts.push('');
										scripts.push('{');

										let roundToVal = (op.props.roundTo?.value || 0) >= 0 ? ('1' + '0'.repeat(op.props.roundTo?.value || 0)) : ('0.' + '0'.repeat(Math.abs(op.props.roundTo?.value) - 1) + '1');
										scripts.push(`let formatNumber = (num) => (Math.floor(num * ${roundToVal}) / ${roundToVal}).toString().replace(/^[+-]?\\d+/, (int) => int.replace(/(\\d)(?=(\\d{3})+$)/g, '$1${op.props.delimeter?.value || ' '}'))`);
										if (field) {
											scripts.push(`${arrPath}.map(f => f.${field} = formatNumber(f.${field}))`);
										} else {
											scripts.push(`${arrPath} = ${arrPath}.map(f => formatNumber(f))`);
										}
										
										scripts.push('}');
										scripts.push('');
										break;
									}
								}
								
								break;
							}
						case 'mapping':
							{
								scripts.push('');
								scripts.push('//MAPPING');

								let defaultValue = op.props.default?.value;
								if (op.props.values?.value?.length || defaultValue) {
									let mappings = op.props.values?.value || [];
									let field = op.props.field?.value;
									let itemValue = field ? `f.${field}` : 'f';

									scripts.push(``);
									scripts.push(`//Mapping values processing`);
									scripts.push(`{`);
									scripts.push(`let mappings = [`);
									mappings.map(m => scripts.push(` ${JSON.stringify(m)}, `));
									scripts.push(`]`);

									if (mappings.some(f => f.criteria)) {
										scripts.push(`mappings.map(f => f.check = f.criteria && dataOperations.compileCriteria(f.criteria));`);
									}

									scripts.push(`${arrPath} = (${arrPath}).map(f => {`)

									if (mappings.some(f => f.key)) {
										scripts.push(`let keyValue = mappings.find(m => m.key == ${itemValue});`)
										scripts.push(`if (keyValue) ${field ? `{ f.${field} = keyValue.value; return f; }` : 'return keyValue.value'}; `)
									}

									if (mappings.some(f => f.criteria && f.isArrayCondition)) {
										scripts.push(`let criteriaValue = mappings.find(m => m.check && m.check(m.isArrayCondition ? { ArrayValue: f } : f));`)
										scripts.push(`if (criteriaValue) ${field ? `{ f.${field} = criteriaValue.value; return f; }` : 'return criteriaValue.value'}; `)
									} else if (mappings.some(f => f.criteria)) {
										scripts.push(`let criteriaValue = mappings.find(m => m.check && m.check(f));`)
										scripts.push(`if (criteriaValue) ${field ? `{ f.${field} = criteriaValue.value; return f; }` : 'return criteriaValue.value'}; `)
									}

									if (defaultValue) {
										if (field) {
											scripts.push(`f.${field} = ${JSON.stringify(defaultValue)};`);
											scripts.push(`return f;`);
										} else {
											scripts.push(`return ${JSON.stringify(defaultValue)};`);
										}
									} else {
										scripts.push(`return f;`);
									}

									scripts.push(`});`)
									scripts.push(`}`);
									scripts.push(``);
								}
								break;
							}
					}

					if (hasCondition) {
						scripts.push('}');
						scripts.push(``);
					}

					convertedToValue = convertedToValue || this.convertsToValueOperations.includes(op.name);
				});
			}

			return scripts;
		},
		buildObjectOperations(varName, operations) {
			let scripts = [];
			let isObject = operations.length > 1;
			let processValueFunc = `let processVal = (v, f) => res = v`;
			if (isObject) {
				processValueFunc = `let processVal = (v, f) => res[f] = v`;
			}
			operations.map(f => {
				switch(f.name) {
					case 'get':
						scripts.push(`processVal(${varName}.${f.props.field.value}, '${f.props.field.value}')`);
						break;
				}
			})
			return `await (async function() {\r\n` + 
				`\t${processValueFunc}\r\n` +
				`\tlet res = ${isObject ? '{}' : 'null'};\r\n` +
				`\t${scripts.join(';\r\n')}\r\n`+
				`\treturn res;` +
			`})();`
		},
		toVarName(str) {
			return (str || '').replace(/\.0/g, '').replace(/\$/g, '').replace(/\./g, '_').replace(/ /g, '_') || 'root';
		},
		makeSafePath(path) {
			return (path || '').replace(/\./g, '?.');
		},
		getDateFormatScript(format, utc, funcName) {
			var	token = /d{1,4}|D{1,4}|M{1,4}|m{1,2}|yy(?:yy)?|YY(?:YY)?|([HhMsTt])\1?|[LloSZ]|"[^"]*"|'[^']*'/g;

			let dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
			let dayLongNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

			let monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
			let monthLongNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

			let get = utc ? "getUTC" : "get";
			var	mainFuncs = {
				get: utc ? "getUTC" : "get",
				d: `date["${get}Date"]()`,
				D: `date["${get}Day"]()`,
				m: `date["${get}Month"]()`,
				y: `date["${get}FullYear"]()`,
				H: `date["${get}Hours"]()`,
				M: `date["${get}Minutes"]()`,
				s: `date["${get}Seconds"]()`,
				L: `date["${get}Milliseconds"]()`,
				o: utc ? '0' : 'date.getTimezoneOffset()',
				timezone: `/\\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) (?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[-+]\\d{4})?)\\b/g`,
				timezoneClip: `/[^-+\\dA-Z]/g`,
			}
				
			let flags = {
				d:    `!d`,
				dd:   `pad(!d)`,
				ddd:  `dayNames[!D]`,
				dddd: `dayLongNames[!D]`,
				D:    `!d`,
				DD:   `pad(!d)`,
				DDD:  `dayNames[!D]`,
				DDDD: `dayLongNames[!D]`,
				M:    `!m + 1`,
				MM:   `pad(!m + 1)`,
				MMM:  `monthNames[!m]`,
				MMMM: `monthLongNames[!m]`,
				yy:   `String(!y).slice(2)`,
				yyyy: `!y`,
				YY:   `String(!y).slice(2)`,
				YYYY: `!y`,
				h:    `!H % 12 || 12`,
				hh:   `pad(!H % 12 || 12)`,
				H:    `!H`,
				HH:   `pad(!H)`,
				m:    `!M`,
				mm:   `pad(!M)`,
				s:    `!s`,
				ss:   `pad(!s)`,
				l:    `pad(!L, 3)`,
				L:    `pad(!L > 99 ? Math.round(!L / 10) : !L)`,
				t:    `!H < 12 ? "a"  : "p"`,
				tt:   `!H < 12 ? "am" : "pm"`,
				T:    `!H < 12 ? "A"  : "P"`,
				TT:   `!H < 12 ? "AM" : "PM"`,
				Z:    utc ? "UTC" : `(String(date).match(!timezone) || [""]).pop().replace(!timezoneClip, "")`,
				o:    `(!o > 0 ? "-" : "+") + pad(Math.floor(Math.abs(!o) / 60) * 100 + Math.abs(!o) % 60, 4)`,
				S:    `["th", "st", "nd", "rd"][!d % 10 > 3 ? 0 : (!d % 100 - !d % 10 != 10) * !d % 10]`
			};

			let script = [];

			script.push(`let ${funcName || 'formatFunc'} = (date) => {`)

			let res = (format || '').replace(token, ($0) => {
				let res = $0 in flags ? flags[$0] : $0.slice(1, $0.length - 1);
				console.log($0);
				Object.keys(mainFuncs).map(f => {
					res = res.replace(`!${f}`, mainFuncs[f]);
				});
				return `\$\{${res}\}`;
			});

			if (res.indexOf('dayNames') > -1) {
				script.push(`let dayNames = ${JSON.stringify(dayNames)}`);
			}

			if (res.indexOf('dayLongNames') > -1) {
				script.push(`let dayLongNames = ${JSON.stringify(dayLongNames)}`);
			}

			if (res.indexOf('monthNames') > -1) {
				script.push(`let monthNames = ${JSON.stringify(monthNames)}`);
			}

			if (res.indexOf('monthLongNames') > -1) {
				script.push(`let monthLongNames = ${JSON.stringify(monthLongNames)}`);
			}

			if (res.indexOf('pad(') > -1) {
				script.push(`let pad = function (val, len) {`);
				script.push(`val = String(val);`);
				script.push(`len = len || 2;`);
				script.push(`while (val.length < len) val = "0" + val;`);
				script.push(`return val;`);
				script.push(`};`);
			}

			script.push(`return \`${res}\``);

			script.push('}')

			return script;
		},
		filterPreviewOperations(arr, operations, preventPreview) {
			operations = operations || arr.operations || [];

			if (preventPreview) return operations.filter(f => !f.disabled);

			let previewArr = this.arrayPreviews.find(f => f.array === arr);
			let previewOp = null;
			if (previewArr) {
				let previewIdx = operations.findIndex(f => f === previewArr.operation);
				if (previewIdx > -1) {
					previewOp = operations[previewIdx];
					return operations.slice(0, previewIdx + 1);
				}
			}

			return operations.filter(f => !f.disabled || previewOp === f);
		},
		async calcResults(resScript, preventPreview) {
			let processInfo = (f) => {
				let scripts = [];
				let arrayPaths = f.arrays.map(f => f.fullPath);

				let declaredNonAchievableArrays = {};
				let processNonAchievableFields = () => {
					let nonAchievableFields = f.fields.filter(af => af.arrays.filter(afa => !arrayPaths.includes(afa.fullPath)).length > 0);

					if (__DEBUG__) {
						console.debug('nonAchievableFields', { nonAchievableFields });
					}
					
					if (nonAchievableFields.length) {
						scripts.push('');
						scripts.push('let resultItem = results.slice(-1)[0];');
						
						nonAchievableFields.map(naf => {
							scripts.push('');
							let resultVarName = this.toVarName(naf.keyPath) + '_results';

							scripts.push(`//PROCESSING: ${this.toVarName(naf.name)}`);
							scripts.push(`let ${resultVarName} = [];`);

							let arrays = naf.arrays.map((f, i) => ({ rIdx: i, arr: f })).filter(f => !arrayPaths.includes(f.arr.fullPath));
							let lastArr = null;
							let processNestedArr = (idx) => {
								let item = arrays[idx];
								let rIdx = item.rIdx;
								let arr = item.arr;
								let parentArrName = rIdx > 0 ? this.toVarName(naf.arrays[rIdx - 1].fullPath) : '';
								let path = this.makeSafePath(rIdx === 0 ? arr.fullPath : `${parentArrName}_row${arr.path}`);
								let arrName = this.toVarName(arr.fullPath);

								if (!declaredNonAchievableArrays[path]) {
									scripts.push(`let ${arrName} = ${path};`);
									declaredNonAchievableArrays[path] = true;
								}
								let arrOperations = this.buildArrayOperations(arrName, this.filterPreviewOperations(arr, void 0, preventPreview).filter(f => !this.convertsToValueOperations.includes(f.name)));
								scripts.push(...arrOperations);
								scripts.push(`for(let ${arrName}_idx = 0; ${arrName}_idx < ${arrName}.length; ${arrName}_idx++) {`);
								scripts.push(`let ${arrName}_row = ${arrName}[${arrName}_idx];`)
								if (idx === arrays.length - 1) {
									let arrayPath = this.makeSafePath(naf.keyPath.split('.0').slice(-1)[0]);
									if (arrayPath) {
										scripts.push(`${resultVarName}.push(${arrName}_row${arrayPath});`);
									} else {
										scripts.push(`${resultVarName}.push(${arrName}_row);`);
									}
								} else {
									processNestedArr(idx + 1);
								}
								scripts.push(`}`);

								if (idx === arrays.length - 1) {
									lastArr = arr;
								}
							}
							
							processNestedArr(0);

							if (lastArr) {
								let arrOperations = this.buildArrayOperations(resultVarName, this.filterPreviewOperations(lastArr, void 0, preventPreview).filter(f => this.convertsToValueOperations.includes(f.name)));
								let isConvertedToValue = lastArr.operations.some(f => this.convertsToValueOperations.includes(f.name));
								scripts.push(...arrOperations);
								scripts.push(`resultItem['${naf.name}'] = ${isConvertedToValue ? resultVarName : `${resultVarName}.join(', ')`};`);
							}
						});

						scripts.push('');
					}
				}

				let getSelectorsAdditionalFields = () => {
					let achievableFields = f.fields.filter(af => af.arrays.filter(afa => !arrayPaths.includes(afa.fullPath)).length === 0);
					
					let result = achievableFields.map(f => {
						let arr = f.arrays.slice(-1)[0];
						if (arr) {
							let arrName = this.toVarName(arr.fullPath);
							let arrayPath = this.makeSafePath(f.keyPath.split('.0').slice(-1)[0]);
							if (arrayPath) {
								return `"${f.name}": ${arrName}_row${arrayPath}, `;
							} else {
								return `"${f.parentPath.split('.').slice(-1)[0]}": ${arrName}_row, `;
							}
						} else {
							return `"${f.name}": ${this.makeSafePath(f.keyPath)}, `;
						}
					});

					if (__DEBUG__)
						console.debug('Achievable fields', achievableFields, result);

					return result;
				}

				if (!f.arrays?.length) {
					scripts.push(`results.push({`);
					scripts.push(`"${f.name}": ${this.makeSafePath(f.keyPath)}, `);
					scripts.push(...getSelectorsAdditionalFields());
					scripts.push(`});`);

					processNonAchievableFields();
				} else {
					let processArr = (idx, parentArrName) => {
						let arr = f.arrays[idx];
						let arrName = this.toVarName(arr.fullPath);
						let path = this.makeSafePath(idx === 0 ? arr.fullPath : `${parentArrName}_row${arr.path}`);
						scripts.push(`let ${arrName} = ${path} || [];`);
						let arrOperations = this.buildArrayOperations(arrName, this.filterPreviewOperations(arr, void 0, preventPreview));
						scripts.push(...arrOperations);
						scripts.push(`for(let ${arrName}_idx = 0; ${arrName}_idx < ${arrName}.length; ${arrName}_idx++) {`);
						scripts.push(`let ${arrName}_row = ${arrName}[${arrName}_idx];`)

						if (f.arrays.length > idx + 1) {
							processArr(idx + 1, arrName);
						} else {
							let arrayPath = this.makeSafePath(f.keyPath.split('.0').slice(-1)[0]);
							if (arrayPath) {
								scripts.push(`results.push({`);
								scripts.push(`"${f.name}": ${arrName}_row${arrayPath}, `);
								scripts.push(...getSelectorsAdditionalFields());
								scripts.push(`});`);
							} else {
								scripts.push(`results.push({`);
								scripts.push(`"${f.name}": ${arrName}_row, `);
								scripts.push(...getSelectorsAdditionalFields());
								scripts.push(`});`);
							}

							processNonAchievableFields();
						}
						
						scripts.push(`};`)
					}

					processArr(0);
				}

				return scripts;
			}

			const scripts = [];
			scripts.push(`let results = [];`);

			this.info.results.map(f => {
				scripts.push(...processInfo(f));
			});

			if (__DEBUG__)
				console.debug('scripts', { scripts });

			let processScriptsFormatting = (startLevel) => {
				let result = [];
				let currentLevel = startLevel || 0;
				for(let i = 0; i < scripts.length; i++) {
					if (typeof(scripts[i]) !== 'string') continue;

					if (scripts[i].endsWith('}') || scripts[i].endsWith('};') || scripts[i].startsWith('}') || scripts[i].startsWith(']')) {
						currentLevel--;
					}
					result.push('\t'.repeat(currentLevel) + scripts[i] + (scripts[i].endsWith(',') ? '\r\n' : ''));
					if (scripts[i].endsWith('{') || scripts[i].endsWith('[')) {
						currentLevel++;
					}
				}

				return result.join('\r\n');
			}
			
			scripts.push(...this.buildArrayOperations('results', this.filterPreviewOperations(this.info, this.info.resultArrayOperations, preventPreview)));

			if (this.info.formatToCsv) {
				scripts.push('');
				scripts.push('let csvResult = []');
				if (this.info.resultArrayOperations.some(f => this.convertsToValueOperations.includes(f.name))) {
					scripts.push('results = Array.isArray(results) ? results : [results]');
				}
				scripts.push('results.map((r, i) => {');
				scripts.push('if (i === 0) {');
				scripts.push('csvResult.push(Object.keys(r).map(f => f).join("\\t"));');
				scripts.push('}');
				scripts.push('csvResult.push(Object.keys(r).map(k => r[k]).join("\\t"))');
				scripts.push('});');
				scripts.push('results = csvResult.join("\\r\\n")');
				scripts.push('');
			}

			scripts.push(``);
			scripts.push(`return results;`);

			const evalText = 
				`var func = async ($, params, dataOperations) => {\r\n`+
				`${processScriptsFormatting(1)}\r\n` +
				`};\r\n` +
				`func(this.data, this.params, this.dataOperations);`;

			if (__DEBUG__)
				console.debug('Calc results', { info: this.info, evalText, scripts });
			
			if (!preventPreview) {
				let ret = null;
				try {
					ret = await (function() {
						return eval(resScript || evalText);
					}).apply({
						data: this.jsonValue,
						params: null,
						dataOperations
					});
				} catch(ex) {
					ret = `${ex}`;
				}

				this.result = ret;
			}

			this.script = processScriptsFormatting(0);
			this.$emit('script', this.script);

			return this.script;
		}
	}
};
</script>
<style lang="scss">
	@import "main/themes/generated/variables.base.scss";
	.web-services--script-builder {
		user-select: none;

		.result-field {
			padding: 8px;
			background: #efefef;
			border-radius: 4px;
		}

		.add-button {
			padding: 4px;
			min-width: 20px;
			min-height: 20px;
			background: #dedede;
			border-radius: 4px;
			user-select: none;

			> span {
				font-size: 10px;
			}

			&:hover {
				background: #d1d1d1;
			}

			&.active {
				background: $base-accent;
				color: $base-bg;
			}


		}

		.array-item {
			background: rgba(0, 0, 0, 0.02);
			padding: 8px;
			border-radius: 4px;
		}

		.view-area {
			max-height: 70vh;
			overflow: auto;
		}
	}
</style>
